import React, { useState, useEffect } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Box,
    Select,
    MenuItem,
    FormHelperText,
    Typography,
} from "@mui/material";
import * as Yup from "yup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import isGHMC, { tableRowBg } from "../../../Common/helper/helpar";
import { faTrash } from "@fortawesome/free-solid-svg-icons"; 
import TippingFeeStore from "./store/TippingFeeStore";
import { TableLoader } from "../../../Common/ShimmerLoader";
import { TippingFeeFilterForm } from "./tippingFeeFilterForm";
import { useFilterStore } from "../../AssetVehicle/store/useFilterStore";
import { ExportTippingFeeData } from "./ExportTippingFeeData";
const TippingFee = () => {
    const { fetchFilterOwnerTypes, ownerTypes, fetchFilterCollectionTypes, collectionTypes } = useFilterStore()
    const { fetchTippingFee, tippingFeeData, addTippingFeeData, loading, fetchTippingFeeForMT, tippingFeeDataForMT } = TippingFeeStore()
    const [isEditing, setIsEditing] = useState(false);
    const [editedRows, setEditedRows] = useState({});
    const [newRowsCount, setNewRowsCount] = useState(0);
    const [errors, setErrors] = useState({});
    const [editIndex, setEditIndex] = useState(null);
    const [value, setValue] = useState("");
    const isProd = isGHMC();

    useEffect(() => {
        fetchFilterCollectionTypes()
        fetchFilterOwnerTypes()
        fetchTippingFee()
        fetchTippingFeeForMT()
    }, []);

    useEffect(() => {
        if (tippingFeeDataForMT?.length > 0) {
            tippingFeeDataForMT.forEach((item) => {
                setValue(item.tippingFeeForMt?.toString() || "");
            });
        }
    }, [tippingFeeDataForMT]);




    const validationSchema = Yup.object().shape({
        ownerTypeId: Yup.string()
            .required("Owner Type Name is Required"),
        collectionTypeId: Yup.string()
            .required("Collection Type Name is Required"),
        tippingFeePercentage: Yup.number()
            .test("required", "Tipping Fee Percentage is required", (value) => value !== undefined && value !== null && value !== "")
            .typeError("Tipping Fee Percentage is Required")
            .min(0, "Tipping Fee Percentage must be at least 0")
            .max(100, "Tipping Fee Percentage cannot be greater than 100"),
        status: Yup.boolean().required("Status is required"),
    });

    const validateRow = async (rowData, index) => {
        try {
            await validationSchema.validate(rowData, { abortEarly: false });
            setErrors((prev) => ({ ...prev, [index]: {} }));
            return true;
        } catch (err) {
            const newErrors = {};
            err.inner.forEach((error) => {
                newErrors[error.path] = error.message;
            });
            setErrors((prev) => ({ ...prev, [index]: newErrors }));
            return false;
        }
    };

    const handleEditAll = () => {
        setIsEditing(true);
        const newEditedRows = {};
        tippingFeeData.forEach((row, index) => {
            newEditedRows[index] = {
                ownerTypeId: row.ownerTypeId,
                collectionTypeId: row.collectionTypeId,
                status: row.status ? true : false,
                tippingFeePercentage: row.tippingFeePercentage
            };
        });
        setEditedRows(newEditedRows);
    };

    const handleChange = (index, field, value) => {
        let filteredValue = value;
        if (field === "tippingFeePercentage") {
            filteredValue = value.replace(/[^0-9]/g, "");
            filteredValue = filteredValue === "" ? "" : parseInt(filteredValue) || 0; // Allow empty string for validation
        }

        setEditedRows((prev) => {
            const updatedRow = { ...prev[index], [field]: filteredValue };
            return { ...prev, [index]: updatedRow };
        });
        const rowData = { ...editedRows[index], [field]: filteredValue };
        validateRow(rowData, index);
    };

    const handleAddNew = () => {
        const newIndex = tippingFeeData.length + newRowsCount;
        setNewRowsCount((prev) => prev + 1);
        setIsEditing(true);
        const newEditedRows = { ...editedRows };
        tippingFeeData.forEach((row, index) => {
            if (!newEditedRows[index]) {
                newEditedRows[index] = {
                    ownerTypeId: row.ownerTypeId,
                    collectionTypeId: row.collectionTypeId,
                    tippingFeePercentage: row.tippingFeePercentage,
                    status: row.status ? true : false,
                };
            }
        });
        newEditedRows[newIndex] = { ownerTypeId: "", collectionTypeId: "", tippingFeePercentage: "", status: true };
        setEditedRows(newEditedRows);
    };

    const handleDeleteNewRow = (index) => {
        setEditedRows((prev) => {
            const newRows = { ...prev };
            delete newRows[index];
            const reIndexedRows = {};
            let newRowIndex = tippingFeeData.length;
            Object.keys(newRows)
                .sort((a, b) => a - b)
                .forEach((key) => {
                    if (parseInt(key) >= tippingFeeData.length) {
                        reIndexedRows[newRowIndex] = newRows[key];
                        newRowIndex++;
                    } else {
                        reIndexedRows[key] = newRows[key];
                    }
                });
            return reIndexedRows;
        });

        setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[index];
            const reIndexedErrors = {};
            let newRowIndex = tippingFeeData.length;
            Object.keys(newErrors)
                .sort((a, b) => a - b)
                .forEach((key) => {
                    if (parseInt(key) >= tippingFeeData.length) {
                        reIndexedErrors[newRowIndex] = newErrors[key];
                        newRowIndex++;
                    } else {
                        reIndexedErrors[key] = newErrors[key];
                    }
                });
            return reIndexedErrors;
        });

        setNewRowsCount((prev) => prev - 1);
        if (Object.keys(editedRows).length === 1) setEditIndex(null);
    };

    const handleSubmit = async () => {
        const validationPromises = Object.keys(editedRows).map((index) =>
            validateRow(editedRows[index], index)
        );
        const validationResults = await Promise.all(validationPromises);
        console.log(validationResults);
        const hasErrors = !validationResults.every((result) => result === true) ||
            Object.values(errors).some((err) => Object.keys(err).length > 0);

        if (hasErrors) {
            console.log("Validation errors present, API call aborted");
            return;
        }

        // Prepare the final data array with tippingFeePercentage as a number
        const finalData = tippingFeeData.map((row, index) => {
            let updatedRow = { ...row, ...(editedRows[index] || {}) };
            if ("tippingId" in updatedRow && index < tippingFeeData.length) {
                // Keep tippingId only for existing rows and convert tippingFeePercentage to number
                return {
                    ...updatedRow,
                    tippingFeePercentage: parseInt(updatedRow.tippingFeePercentage) || 0,
                };
            }
            // Remove tippingId for new rows and convert tippingFeePercentage to number
            const { tippingId, ...rowWithoutTippingId } = updatedRow;
            return {
                ...rowWithoutTippingId,
                tippingFeePercentage: parseInt(rowWithoutTippingId.tippingFeePercentage) || 0,
            };
        });

        // Add new rows without tippingId and with tippingFeePercentage as number
        Object.keys(editedRows)
            .filter((index) => parseInt(index) >= tippingFeeData.length)
            .forEach((index) => {
                const newRow = { ...editedRows[index], tippingFeeForMT: value || "0" };
                const { tippingId, ...rowWithoutTippingId } = newRow; // Remove tippingId if it exists
                finalData.push({
                    ...rowWithoutTippingId,
                    tippingFeePercentage: parseInt(rowWithoutTippingId.tippingFeePercentage) || 0,
                });
            });

        try {
            const result = await addTippingFeeData(finalData, value);
            if (result.success) {
                fetchTippingFee();
                setIsEditing(false);
                setEditedRows({});
                setErrors({});
                setNewRowsCount(0);
                setEditIndex(null);
            }
        } catch (error) {
            console.error("Error submitting:", error);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditedRows({});
        setErrors({});
        setNewRowsCount(0);
        setEditIndex(null);
    };

    return (
        <div style={{ width: "100%" }}>
            <div className="content">
                <div className="page-content mt-1">
                    <div className="d-flex justify-content-end align-items-end p-2 mt-1">
                        {/* Left Side - Filters */}


                        {/* Right Side - Buttons */}
                        <div className="d-flex gap-2">
                            {!isEditing && (
                                <button
                                    className="button-container"
                                    onClick={handleEditAll}
                                >
                                    Edit All
                                </button>
                            )}
                            {(isEditing || newRowsCount > 0) && (
                                <button
                                    className={`btn w-90 ${isProd ? "search-ghmc" : "search-swm"}`}
                                    style={{ border: "none", width: "160px", borderRadius: "5px" }}
                                    onClick={handleCancel}
                                >
                                    CANCEL
                                </button>
                            )}
                            <ExportTippingFeeData />
                        </div>
                    </div>
                    <div className="d-flex p-2 mt-1 ml-2">
                        <TippingFeeFilterForm />
                    </div>
                    <div className="card bg-white mb-3 ml-3 shadow" style={{ width: "97.2%" }}>
                        <div className="card-body d-flex align-items-center ">
                            <label className="fw-semibold me-3">Tipping Fee For MT</label>
                            <TextField
                                type="text"
                                name="tippingFeeForMT"
                                className="bg-light text-dark border-0"
                                size="small"
                                style={{ width: "100px", height: "30px" }}
                                value={value}
                                disabled={!isEditing}
                                onChange={(e) => {
                                    const inputValue = e.target.value;
                                    if (/^\d*\.?\d*$/.test(inputValue)) {
                                        setValue(inputValue); // Update state only if it's a valid number
                                    }
                                }}
                            />

                        </div>
                    </div>


                    <div >
                        <div >
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="top-header d-flex justify-content-center align-items-center">
                                            <span>Tipping Fee</span>
                                        </div>

                                        <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: "8px", maxHeight: 700 }} >
                                            <Table stickyHeader aria-label="sticky table">
                                                <TableHead >
                                                    <TableRow sx={{ backgroundColor: "#d40000" }}>
                                                        <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}>S.no</TableCell>
                                                        <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}>Owner Type</TableCell>
                                                        <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}>Collection Type</TableCell>
                                                        <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}>Tipping Fee Percentage</TableCell>
                                                        <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}>Per MT In INR</TableCell>
                                                        <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}>Effective From Date</TableCell>
                                                        <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}>Previous Tipping Fee</TableCell>
                                                        <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}>Effective From Date (Previous)</TableCell>
                                                        <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}>Effective To Date (Previous)</TableCell>
                                                        {/* {newRowsCount > 0 && ( */}
                                                        <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`} style={{ minWidth: "150px", width: "200px" }} >Status</TableCell>
                                                        {/* )} */}
                                                        {newRowsCount > 0 && (
                                                            <TableCell className={`btn ${isProd ? "ghmc-table-col" : "swm-table-col"}`}>Action</TableCell>
                                                        )}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {loading ? (
                                                        <TableRow>
                                                            <TableCell colSpan={10} align="center">Loading...</TableCell>
                                                        </TableRow>
                                                    ) : tippingFeeData.length === 0 && newRowsCount === 0 ? (
                                                        <TableRow>
                                                            <TableCell colSpan={10} align="center">No data available</TableCell>
                                                        </TableRow>
                                                    ) : (
                                                        Array.from({ length: tippingFeeData.length + newRowsCount }, (_, index) => index).map((index) => (
                                                            <TableRow
                                                                key={index}
                                                                style={{
                                                                    backgroundColor: tableRowBg(index),
                                                                }}
                                                            >
                                                                <TableCell style={{ border: "1px solid white", textAlign: "center" }}>{index + 1}</TableCell>
                                                                <TableCell style={{ border: "1px solid white", textAlign: "center" }}>
                                                                    {isEditing || editedRows[index] ? (
                                                                        <>
                                                                            <Select
                                                                                value={editedRows[index]?.ownerTypeId || ""}
                                                                                onChange={(e) => handleChange(index, "ownerTypeId", e.target.value)}
                                                                                size="small"
                                                                                error={Boolean(errors[index]?.ownerTypeId)}
                                                                                helperText={errors[index]?.ownerTypeId}
                                                                                displayEmpty
                                                                                style={{ backgroundColor: "#fff" }}
                                                                            // Adjust width as needed
                                                                            >
                                                                                <MenuItem value="">Select</MenuItem>
                                                                                {ownerTypes.map((type) => (
                                                                                    <MenuItem key={type.ownerTypeId} value={type.ownerTypeId}>
                                                                                        {type.ownerTypeName}
                                                                                    </MenuItem>
                                                                                ))}
                                                                            </Select>
                                                                            <Typography color="error">{errors[index]?.ownerTypeId}</Typography>
                                                                        </>
                                                                    ) : (
                                                                        tippingFeeData[index]?.ownerTypeName || ""
                                                                    )}
                                                                </TableCell>
                                                                <TableCell style={{ border: "1px solid white", textAlign: "center" }}>
                                                                    {isEditing || editedRows[index] ? (
                                                                        <>
                                                                            <Select
                                                                                value={editedRows[index]?.collectionTypeId || ""}
                                                                                onChange={(e) => handleChange(index, "collectionTypeId", e.target.value)}
                                                                                error={Boolean(errors[index]?.collectionTypeId)}
                                                                                helperText={errors[index]?.collectionTypeId}
                                                                                displayEmpty
                                                                                size="small"
                                                                                style={{ backgroundColor: "#fff" }}
                                                                            >
                                                                                <MenuItem value="">Select</MenuItem>
                                                                                {collectionTypes.map((type) => (
                                                                                    <MenuItem key={type.collectionTypeId} value={type.collectionTypeId}>
                                                                                        {type.collectionTypeName}
                                                                                    </MenuItem>
                                                                                ))}
                                                                            </Select>
                                                                            <Typography color="error">{errors[index]?.collectionTypeId}</Typography>
                                                                        </>
                                                                    ) : (
                                                                        tippingFeeData[index]?.collectionTypeName || ""
                                                                    )}
                                                                </TableCell>
                                                                <TableCell style={{ border: "1px solid white", textAlign: "center" }}>
                                                                    {isEditing || editedRows[index] ? (
                                                                        <>
                                                                            <TextField
                                                                                value={editedRows[index]?.tippingFeePercentage || ""}
                                                                                onChange={(e) => handleChange(index, "tippingFeePercentage", e.target.value)}
                                                                                size="small"
                                                                                error={Boolean(errors[index]?.tippingFeePercentage)}
                                                                            // helperText={errors[index]?.tippingFeePercentage}
                                                                            />
                                                                            <Typography color="error">{errors[index]?.tippingFeePercentage}</Typography>
                                                                        </>
                                                                    ) : (
                                                                        tippingFeeData[index]?.tippingFeePercentage || ""
                                                                    )}
                                                                </TableCell>
                                                                <TableCell style={{ border: "1px solid white", textAlign: "center" }}>
                                                                    {
                                                                        tippingFeeData[index]?.perMtinR
                                                                    }
                                                                </TableCell>
                                                                <TableCell style={{ border: "1px solid white", textAlign: "center" }}>
                                                                    {
                                                                        tippingFeeData[index]?.effectiveFromDate || "N/A"
                                                                    }
                                                                </TableCell>
                                                                <TableCell style={{ border: "1px solid white", textAlign: "center" }}>
                                                                    {
                                                                        tippingFeeData[index]?.previousTippingFee || "N/A"
                                                                    }
                                                                </TableCell>
                                                                <TableCell style={{ border: "1px solid white", textAlign: "center" }}>
                                                                    {
                                                                        tippingFeeData[index]?.previousEffectiveFromDate || "N/A"
                                                                    }
                                                                </TableCell>
                                                                <TableCell style={{ border: "1px solid white", textAlign: "center" }}>
                                                                    {
                                                                        tippingFeeData[index]?.previousEffectiveToDate || "N/A"
                                                                    }
                                                                </TableCell>
                                                                {/* {newRowsCount > 0 && ( */}
                                                                <TableCell style={{ border: "1px solid white", textAlign: "center" }}>
                                                                    {isEditing || editedRows[index] ? (
                                                                        <select
                                                                            value={editedRows[index]?.status ?? true}
                                                                            className="form-select"
                                                                            style={{
                                                                                width: "100%",
                                                                                padding: "8px",
                                                                                fontSize: "14px",
                                                                                border: "1px solid #ccc",
                                                                                borderRadius: "6px",
                                                                                backgroundColor: "#fff",
                                                                                cursor: "pointer",
                                                                                outline: "none",
                                                                                appearance: "none",
                                                                                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
                                                                                textAlign: 'center'
                                                                            }}
                                                                            onChange={(e) => handleChange(index, "status", e.target.value === "true")}
                                                                        >
                                                                            <option value={true}>Active</option>
                                                                            <option value={false}>InActive</option>
                                                                        </select>
                                                                    ) : tippingFeeData[index]?.status ? (
                                                                        "Active"
                                                                    ) : (
                                                                        "InActive"
                                                                    )}
                                                                </TableCell>
                                                                {/* )} */}
                                                                {newRowsCount > 0 && (
                                                                    <TableCell style={{ border: "1px solid white", textAlign: "center" }}>
                                                                        {index >= tippingFeeData.length && (
                                                                            <button
                                                                                className={`${index % 2 === 0 ? "bg-F8CCCD" : "bg-ddefff"} ${isProd ? "edit-ghmc" : "edit-swm"}`}
                                                                                onClick={() => handleDeleteNewRow(index)}
                                                                            >
                                                                                <FontAwesomeIcon icon={faTrash} />
                                                                            </button>
                                                                        )}
                                                                    </TableCell>
                                                                )}
                                                            </TableRow>
                                                        ))
                                                    )}

                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Box sx={{ marginTop: "15px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                        <button
                            className="button-container"
                            onClick={handleAddNew}
                        >
                            + ADD NEW
                        </button>
                        <button
                            className="deployment-button"
                            onClick={handleSubmit}
                            hidden={Object.keys(editedRows).length === 0}
                        >
                            SAVE
                        </button>
                    </Box>
                </div>
            </div>
        </div>
    );
};

export default TippingFee;